import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../api/axiosConfig";
import { format, differenceInDays } from "date-fns";
import * as XLSX from "xlsx";

const TimesheetBarChart = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [xAxisLabel, setXAxisLabel] = useState("Day");
  const [selectedMode, setSelectedMode] = useState("auto"); // "auto" | "daily" | "weekly" | "monthly"

  useEffect(() => {
    if (!startDate || !endDate) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setStartDate(start);
      setEndDate(today);
    } else {
      fetchData();
    }
  }, [startDate, endDate, selectedMode]);

  const fetchData = async () => {
    try {
      if (!startDate || !endDate) return;

      const daysDifference = differenceInDays(
        new Date(endDate),
        new Date(startDate)
      );

      let mode = "daily";
      let label = "Day";

      if (selectedMode !== "auto") {
        mode = selectedMode;
        label = selectedMode.charAt(0).toUpperCase() + selectedMode.slice(1);
      } else {
        if (daysDifference > 7 && daysDifference <= 31) {
          mode = "weekly";
          label = "Week";
        } else if (daysDifference > 31 && daysDifference <= 366) {
          mode = "monthly";
          label = "Month";
        } else if (daysDifference > 366) {
          mode = "yearly";
          label = "Year";
        }
      }

      setXAxisLabel(label);
      const url = `/api/hours-all/${mode}/${format(
        startDate,
        "yyyy-MM-dd"
      )}/${format(endDate, "yyyy-MM-dd")}`;
      const response = await api.get(url);

      const filteredData = (
        Array.isArray(response.data) ? response.data : []
      ).filter(
        (item) =>
          convertToHours(item.nonProductiveHours) > 0 ||
          convertToHours(item.productiveHours) > 0 ||
          convertToHours(item.vo_plannedHours) > 0 ||
          convertToHours(item.plannedHoursForEstimation) > 0
      );

      setChartData(filteredData);
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setSelectedMode("auto");
    if (endDate && date > endDate) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    setSelectedMode("auto");
    if (startDate && date < startDate) {
      alert("End date cannot be before start date.");
      return;
    }
    setEndDate(date);
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedMode("auto");
  };

  const isSameMonthRange = (start, end) =>
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  const convertToHours = (timeValue) => {
    if (!timeValue) return 0;

    if (typeof timeValue === "string" && timeValue.includes(":")) {
      const [hours, minutes] = timeValue.split(":").map(Number);
      return parseFloat((hours + minutes / 60).toFixed(2));
    }

    if (typeof timeValue === "number") {
      return parseFloat(timeValue.toFixed(2));
    }

    return 0;
  };

  const getXAxisLabels = () => {
    return chartData.map((item) => item.day);
  };

  const seriesData = [
    {
      name: "Available",
      type: "bar",
      itemStyle: { color: "#1f77b4" },
      data: chartData.map(
        (d) =>
          convertToHours(d.nonProductiveHours) +
          convertToHours(d.productiveHours)
      ),
      label: {
        show: true,
        position: "top",
        color: "#000",
        fontSize: 12,
      },
    },
    {
      name: "Sold",
      type: "bar",
      itemStyle: { color: "#ff7f0e" },
      data: chartData.map(
        (d) =>
          convertToHours(d.plannedHoursForEstimation) +
          convertToHours(d.vo_plannedHours)
      ),
      label: {
        show: true,
        position: "top",
        color: "#000",
        fontSize: 12,
      },
    },
  ];

  const exportToExcel = () => {
    if (chartData.length === 0) {
      alert("No data available to export.");
      return;
    }

    const ws_data = chartData.map((data) => [
      data.day,
      data.nonProductiveHours,
      data.productiveHours,
      data.vo_plannedHours,
      data.plannedHoursForEstimation,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([
      [
        "Day",
        "Non Productive Hours",
        "Productive Hours",
        "VO Planned Hours",
        "Planned Hours for Estimation",
      ],
      ...ws_data,
    ]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Raw Data");

    const chartDataFormatted = [
      ["Day", "Available Hours", "Sold Hours"],
      ...chartData.map((item) => [
        item.day,
        convertToHours(item.nonProductiveHours) +
          convertToHours(item.productiveHours),
        convertToHours(item.plannedHoursForEstimation) +
          convertToHours(item.vo_plannedHours),
      ]),
    ];

    const chartWs = XLSX.utils.aoa_to_sheet(chartDataFormatted);
    XLSX.utils.book_append_sheet(wb, chartWs, "Available Vs Sold");

    XLSX.writeFile(wb, "Timesheet_Data_with_Chart.xlsx");
  };

  return (
    <div className="project-bar-p-vs-np-report">
      <div className="report-date-con">
        <label>Start Date: </label>
        <DatePicker
          className="project-barchart-datepicker"
          dateFormat={"dd-MM-yyyy"}
          selected={startDate}
          onChange={handleStartDateChange}
          placeholderText="Select Start Date"
        />

        <label>End Date: </label>
        <DatePicker
          className="project-barchart-datepicker"
          dateFormat={"dd-MM-yyyy"}
          selected={endDate}
          onChange={handleEndDateChange}
          placeholderText="Select End Date"
        />

        <button onClick={handleClear} className="chart-clear-btn">
          Clear
        </button>

        <button onClick={exportToExcel} className="chart-export-btn">
          Export to Excel
        </button>

        {startDate && endDate && isSameMonthRange(startDate, endDate) && (
          <>
            <label style={{ marginLeft: "1rem" }}>Mode: </label>
            <select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value)}
              className="chart-mode-select"
            >
              <option value="auto">Auto</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </>
        )}
      </div>

      <ReactECharts
        option={{
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
          legend: { data: ["Available", "Sold"] },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "8%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            name: xAxisLabel,
            nameLocation: "middle",
            nameGap: 80,
            data: getXAxisLabels(),
            axisLabel: { rotate: 90 },
          },
          yAxis: {
            type: "value",
            name: "Hours",
            nameLocation: "middle",
            nameGap: 50,
          },
          series: seriesData,
        }}
        style={{
          height: "500px",
          width: "100%",
          backgroundColor: "transparent",
        }}
      />
    </div>
  );
};

export default TimesheetBarChart;
